import Channels, { IChannel } from '@/models/Channels'

import { isChannelRestricted } from '@/helper/channel.helper'
import { ChannelRoles } from '@/models/Users'
import { ChannelsService } from '@/service/ChannelsService'
import { reactive } from 'vue'
import { GroupState } from './groups'

interface ChannelMetadata {
  managers: {
    [channelId: string]: Array<{
      contactId: string
      fullName?: string
      avatar?: string
      role: string
    }>
  }
  unreadCounts: {
    [channelId: string]: number
  }
  lastReadTimestamps: {
    [channelId: string]: Date
  }
}

export class ChannelsState {
  state = reactive({
    processing: false,
    currentChannelData: {} as any,
    hasAnnouncementRestriction: false, // to check if the current channel has announcement restriction
    currentFetchPromise: null as Promise<void> | null,
    channelMetadata: {} as ChannelMetadata,
    isLoadingMetadata: false,
  })

  async fetchChannels(noCache?: boolean): Promise<void> {
    const channels = await ChannelsService.fetchAllChannels(noCache)

    // Use GroupState to set channels and handle distribution
    GroupState.setChannels(channels)

    // Fetch metadata separately
    this.fetchChannelsMetadata()
  }

  async fetchChannelsMetadata(): Promise<void> {
    try {
      this.state.isLoadingMetadata = true
      const metadata = await ChannelsService.fetchChannelsMetadata()

      // Convert ISO strings to Date objects for lastReadTimestamps
      if (metadata.lastReadTimestamps) {
        Object.keys(metadata.lastReadTimestamps).forEach(channelId => {
          if (metadata.lastReadTimestamps[channelId]) {
            metadata.lastReadTimestamps[channelId] = new Date(
              metadata.lastReadTimestamps[channelId]
            )
          }
        })
      }

      this.state.channelMetadata = metadata

      // Update existing channels with metadata
      if (GroupState.state.channelList) {
        GroupState.state.channelList = GroupState.state.channelList.map(
          (channel: any) => {
            return {
              ...channel,
              managers: metadata.managers[channel._id] || [],
              unreadCount: metadata.unreadCounts[channel._id] || 0,
              lastReadTimestamp:
                metadata.lastReadTimestamps[channel._id] || null,
            }
          }
        )
      }

      // Update current channel if one is selected
      if (this.state.currentChannelData?._id) {
        this.state.currentChannelData = {
          ...this.state.currentChannelData,
          managers: metadata.managers[this.state.currentChannelData._id] || [],
          unreadCount:
            metadata.unreadCounts[this.state.currentChannelData._id] || 0,
          lastReadTimestamp:
            metadata.lastReadTimestamps[this.state.currentChannelData._id] ||
            null,
        }
      }
    } catch (error) {
      console.error('Error fetching channel metadata:', error)
    } finally {
      this.state.isLoadingMetadata = false
    }
  }

  setCurrentChannel(slug: string): any {
    if (!slug) {
      return
    }
    const channel = GroupState.state.channelList?.find(
      (channel: any) => channel.slug === slug
    )
    if (channel) {
      // Get metadata for this channel
      const managers = this.state.channelMetadata?.managers?.[channel._id] || []
      const unreadCount =
        this.state.channelMetadata?.unreadCounts?.[channel._id] || 0
      const lastReadTimestamp =
        this.state.channelMetadata?.lastReadTimestamps?.[channel._id] || null

      this.state.currentChannelData = {
        ...channel,
        managers,
        unreadCount,
        lastReadTimestamp,
      }
      this.state.hasAnnouncementRestriction = isChannelRestricted(channel)
    }
    return channel
  }

  async createChannel(channelData: IChannel): Promise<any> {
    const channel = new Channels(channelData)
    await channel.save()
    GroupState.state.channelList.push(channel.data)
    this.state.currentChannelData = channel
    return channel.data
  }

  async updateChannel(channelData: IChannel): Promise<void> {
    this.state.currentChannelData.name = channelData.name
    this.state.currentChannelData.description = channelData.description
    this.state.currentChannelData.icon = channelData.icon
    this.state.currentChannelData.isAnnouncement = channelData.isAnnouncement
    ChannelsService.update(this.state.currentChannelData)
    // update the channel in the channel list
    GroupState.state.channelList = GroupState.state.channelList?.map(
      (channel: any) => {
        if (channel._id === this.state.currentChannelData._id) {
          return this.state.currentChannelData
        }
        return channel
      }
    )
  }

  async updateChannelAnnouncement(isEnabled: boolean): Promise<void> {
    this.state.currentChannelData.isAnnouncement = isEnabled
    ChannelsService.update(this.state.currentChannelData)
  }

  async deleteChannel(): Promise<void> {
    await ChannelsService.delete(this.state.currentChannelData)
    GroupState.state.channelList = GroupState.state.channelList.filter(
      (channel: any) => channel.slug !== this.state.currentChannelData.slug
    )
  }

  updateChannelUnreadCount(channelId: string, newUnreadCount: number): void {
    // Update in channelMetadata
    if (this.state.channelMetadata?.unreadCounts) {
      this.state.channelMetadata.unreadCounts[channelId] = newUnreadCount
    }

    // Update in channelList
    const channel = GroupState.state.channelList?.find(
      ch => ch._id === channelId
    )
    if (channel) {
      channel.unreadCount = newUnreadCount
    }

    // Update in currentChannelData if it's the same channel
    if (this.state.currentChannelData?._id === channelId) {
      this.state.currentChannelData.unreadCount = newUnreadCount
    }
  }

  async fetchChannelMembers(
    paginationObject = { limit: 10, pageNo: 1 },
    roles?: Array<ChannelRoles>,
    searchText?: string,
    fetchingForMention?: boolean,
    channelData?: any
  ): Promise<any> {
    const channelMembers = await ChannelsService.fetchChannelMembers(
      channelData ? channelData._id : this.state.currentChannelData._id,
      roles?.join(','),
      paginationObject,
      searchText
    )
    if (fetchingForMention) {
      return channelMembers
    }
    if (
      paginationObject.pageNo > 1 &&
      ChannelState.state.currentChannelData.channelMembers
    )
      ChannelState.state.currentChannelData.channelMembers = [
        ...ChannelState.state.currentChannelData.channelMembers,
        ...channelMembers,
      ]
    else ChannelState.state.currentChannelData.channelMembers = channelMembers
  }
  async addChannelMembers(members: Array<any>): Promise<any> {
    const membersData = members.map(m => {
      return {
        contactId: m.contactId,
        role: ChannelRoles.MEMBER,
        notify: true,
      }
    })
    const response = await ChannelsService.addChannelMembers(
      membersData,
      this.state.currentChannelData._id
    )
    return response
  }

  async addChannelManagers(members: Array<any>): Promise<any> {
    const membersData = members.map(m => {
      return {
        contactId: m.contactId,
        role: ChannelRoles.MANAGER,
        notify: true,
      }
    })
    const response = await ChannelsService.addChannelMembers(
      membersData,
      this.state.currentChannelData._id
    )
    return response
  }

  async updateUserRoleInChannel(
    contactId: string,
    role: ChannelRoles
  ): Promise<any> {
    const response = await ChannelsService.updateMemberRole(
      this.state.currentChannelData._id,
      contactId,
      role
    )

    const member = this.state.currentChannelData.channelMembers.find(
      (m: any) => m.contactId === contactId
    )

    if (member) {
      member.role = role
    }

    // Update metadata managers list
    const channelId = this.state.currentChannelData._id
    if (role === ChannelRoles.MANAGER) {
      if (!this.state.channelMetadata.managers[channelId]) {
        this.state.channelMetadata.managers[channelId] = []
      }
      // Add to managers if not already present
      if (
        !this.state.channelMetadata.managers[channelId].find(
          m => m.contactId === contactId
        )
      ) {
        this.state.channelMetadata.managers[channelId].push({
          contactId,
          fullName: member?.fullName,
          avatar: member?.avatar,
          role: ChannelRoles.MANAGER,
        })
      }
    } else if (role === ChannelRoles.MEMBER) {
      // Remove from managers list
      if (this.state.channelMetadata.managers[channelId]) {
        this.state.channelMetadata.managers[channelId] =
          this.state.channelMetadata.managers[channelId].filter(
            m => m.contactId !== contactId
          )
      }
    }

    // Update current channel managers
    this.state.currentChannelData.managers =
      this.state.channelMetadata.managers[channelId] || []

    return response
  }
  async removeUserFromChannel(contactId: string): Promise<any> {
    const response = await ChannelsService.removeUserFromChannel(
      this.state.currentChannelData._id,
      contactId
    )

    this.state.currentChannelData.membersCount -= 1
    // no need to fetch members all over again, simply delete the member from the channel members list
    const memberIndex = this.state.currentChannelData.channelMembers.findIndex(
      (m: any) => m.contactId === contactId
    )
    if (memberIndex > -1) {
      this.state.currentChannelData.channelMembers.splice(memberIndex, 1)
    }

    const managerIndex = this.state.currentChannelData.managers.findIndex(
      (m: any) => m.contactId === contactId
    )
    if (managerIndex > -1) {
      this.state.currentChannelData.managers.splice(managerIndex, 1)
    }

    return response
  }
  async leaveChannel(channelId: string): Promise<any> {
    const response = await ChannelsService.leaveChannel(channelId)
    return response
  }

  async updateVisibility(
    channelId: string,
    visibility = 'PRIVATE'
  ): Promise<any> {
    const response = await ChannelsService.updateChannelVisibility(
      channelId,
      visibility
    )
    return response
  }

  async markChannelAsRead(slug: string): Promise<any> {
    // if user is not active member, do not mark channel as read
    if (!GroupState.isActiveMember()) {
      return
    }
    try {
      // find channelId from channel slug
      const channelData = GroupState.state.channelList?.find(
        (channel: any) => channel.slug === slug
      )
      if (!channelData) {
        throw new Error('Channel not found')
      }

      // Optimistically update local state
      channelData.unreadCount = 0
      const now = new Date()
      channelData.lastReadTimestamp = now

      // Update metadata
      if (!this.state.channelMetadata.lastReadTimestamps) {
        this.state.channelMetadata.lastReadTimestamps = {}
      }
      this.state.channelMetadata.lastReadTimestamps[channelData._id] = now

      // Call the backend service
      const response = await ChannelsService.markChannelAsRead(channelData._id)

      return response
    } catch (error: any) {
      console.error(
        'Error marking channel as read:',
        error.response?.data ?? error.message
      )
      throw error
    }
  }
}

export const ChannelState = new ChannelsState()
